interface BatcherOptions {
  maxBatchSize?: number
  maxBatchTime?: number
}

interface Batch<Args, Result> {
  calls: Args[]
  startTimestamp: number
  runTimeoutId?: number
  flushPromise: Promise<void>
  onFlush: () => void
  resultsPromise?: Promise<Result[]>
  id: number
}

export interface Batcher<Args extends any[], Result> {
  run: (...args: Args) => Promise<Result>
}

let batchCounter = 0

export function createBatcher<Args extends any[], Result>(
  fn: (batch: Args[]) => Promise<Result[]>,
  { maxBatchSize = 32, maxBatchTime = 100 }: BatcherOptions = {}
): Batcher<Args, Result> {
  let assemblingBatch: Batch<Args, Result> | null = null

  const createBatch = () => {
    let batchId = ++batchCounter

    let onFlush = () => {}

    const flushPromise = new Promise<void>(resolve => {
      onFlush = resolve
    })

    const batch: Batch<Args, Result> = {
      calls: [],
      startTimestamp: Date.now(),
      flushPromise,
      onFlush,
      id: batchId,
    }

    return batch
  }

  const run = async (...args: Args) => {
    if (assemblingBatch === null) {
      assemblingBatch = createBatch()
    }

    const targetBatch = assemblingBatch

    const entryIndex = targetBatch.calls.length
    targetBatch.calls.push(args)
    const results = await waitForBatch(targetBatch)
    return results[entryIndex]
  }

  const waitForBatch = async (targetBatch: Batch<Args, Result>): Promise<Result[]> => {
    if (targetBatch.resultsPromise) {
      return targetBatch.resultsPromise
    }

    if (typeof targetBatch.runTimeoutId === 'number') {
      clearTimeout(targetBatch.runTimeoutId)
    }

    const timeSinceStart = Date.now() - targetBatch.startTimestamp
    if (targetBatch.calls.length >= maxBatchSize || timeSinceStart >= maxBatchTime) {
      targetBatch.resultsPromise = fn(targetBatch.calls)
      if (assemblingBatch === targetBatch) assemblingBatch = null
      targetBatch.onFlush()
      return targetBatch.resultsPromise
    }

    const timeToFlush = maxBatchTime - timeSinceStart
    targetBatch.runTimeoutId = setTimeout(() => {
      targetBatch.resultsPromise = fn(targetBatch.calls)
      if (assemblingBatch === targetBatch) assemblingBatch = null
      targetBatch.onFlush()
    }, timeToFlush)

    await targetBatch.flushPromise

    if (targetBatch.resultsPromise) return targetBatch.resultsPromise

    throw new Error('Batcher error')
  }

  return { run }
}
